// src/components/energy-reset/ArtifactRail.tsx
import { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  total: number;
  day: number;                 // текущий день курса (1..total), 0 = предстарт
  doneToday: boolean;
  onArtifactClick?: (index: number) => void;
  highlightIndex?: number | null; // какой элемент подсветить
  pulseKey?: number;              // меняется -> пульс у highlightIndex
  artifactsByDay?: Map<number, { emoji: string | null }>;
  // QUEUE-UI-A (21.09.2026): дни, чья отметка лежит в офлайн-очереди
  // (utils/queue.ts) и ещё не доставлена серверу — значок "ждёт отправки"
  // в свободном углу кружка, независимо от collected/emoji.
  queuedDays?: Set<number>;
};


export default function ArtifactRail({
  total,
  day,
  doneToday,
  onArtifactClick,
  highlightIndex = null,
  pulseKey,
  artifactsByDay,
  queuedDays,
}: Props) {
  const items = useMemo(() => Array.from({ length: total }, (_, i) => i + 1), [total]);

  /** ====== Карусель: рассчитываем ширину айтема так, чтобы на мобиле было видно ровно 7 шт. ====== */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [itemW, setItemW] = useState<number | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const recalc = () => {
      const cs = getComputedStyle(el);
      const gap = parseFloat(cs.columnGap || "8") || 8; // gap-2 ~= 8px
      const width = el.clientWidth; // видимая ширина скролл-области (без внешних паддингов)
      const w = Math.max(40, Math.floor((width - gap * 6) / 7)); // 7 элементов в окне, минимум 40px
      setItemW(w);
      // прокрутка к активному дню при ресайзе
      scrollToActive(el, w, gap);
    };

    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollRef, total]);

  /** Автоцентрирование активного дня в окне из 7 элементов */
  const scrollToActive = (container: HTMLDivElement, width: number, gap: number) => {
    // индексы 0..total-1
    const activeIdx = Math.max(0, Math.min(total - 1, (day || 1) - 1));
    // хотим активный примерно в центре окна из 7: смещение на 3 элемента влево
    const startIdx = Math.max(0, Math.min(activeIdx - 3, Math.max(0, total - 7)));
    const x = startIdx * (width + gap);
    container.scrollTo({ left: x, behavior: "smooth" });
  };

  // при изменении day — центрируем
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || itemW == null) return;
    const cs = getComputedStyle(el);
    const gap = parseFloat(cs.columnGap || "8") || 8;
    scrollToActive(el, itemW, gap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, itemW, total]);

  return (
    // фон контейнера прозрачный
    <div className="rounded-2xl p-3 bg-transparent border-0 shadow-none">
      <style>{`
        @keyframes arcGlow {
          0% { box-shadow: 0 0 0 0 rgba(251,191,36,0), inset 0 0 0 0 rgba(251,191,36,0); transform: scale(1); }
          40% { box-shadow: 0 0 0 10px rgba(251,191,36,.25), inset 0 0 0 0 rgba(251,191,36,0); transform: scale(1.08); }
          100% { box-shadow: 0 0 0 0 rgba(251,191,36,0), inset 0 0 0 0 rgba(251,191,36,0); transform: scale(1); }
        }
        /* Скрыть горизонтальный скроллбар на мобильных */
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div
  ref={scrollRef}
  tabIndex={-1}  // ← убираем из таб-порядка и кликом тоже не фокусируется
  className="
    flex items-center gap-2
    overflow-x-auto overflow-y-visible no-scrollbar
    snap-x snap-mandatory touch-pan-x overscroll-x-contain
    py-1
    outline-none focus:outline-none focus-visible:outline-none focus-within:outline-none
  "
  aria-label="Лента артефактов по дням"
  role="list"
>

        {items.map((i) => {
          const isToday = day === i && !doneToday; // текущий день, но ещё не выполнен
          const collected = day > 0 && (i < day || (i === day && doneToday)); // уже собран
          const isHighlight = highlightIndex === i;

          const base =
            "relative rounded-full border flex items-center justify-center font-semibold transition " +
            "snap-center select-none";

          // фолбэк до вычисления: чуть крупнее текст
          const sizeCls = itemW ? undefined : "h-10 w-10 text-[16px]";

          const cls =
            collected
              ? "bg-emerald-50 border-emerald-300 text-emerald-700"
              : isToday
              ? "bg-amber-50 border-amber-300 text-amber-700 ring-2 ring-amber-200"
              : "bg-white border-gray-200 text-gray-400";

          const pulseClass =
            isHighlight && pulseKey !== undefined ? "animate-[arcGlow_600ms_ease-out]" : "";

          const label = collected ? "Собрано" : isToday ? "Сегодня" : "Закрыто";

          const emoji = artifactsByDay?.get(i)?.emoji ?? null;
          const isQueued = queuedDays?.has(i) ?? false;

          // ⬇️ НОВОЕ: более крупный адаптивный шрифт внутри кружка
          // ~45% от диаметра, с пределами, чтобы не ломать двузначные числа и не налезать на бейдж
          const fontSizePx =
            itemW != null ? Math.max(16, Math.min(24, Math.floor(itemW * 0.45))) : undefined;

          return (
            <button
              key={`${i}-${pulseKey ?? "x"}`} // key включает pulseKey, чтобы перезапускать анимацию
              type="button"
              role="listitem"
              className={`${base} ${cls} ${pulseClass} ${sizeCls ?? ""}`}
              aria-label={`Артефакт ${i}: ${label}`}
              aria-current={isToday ? "true" : undefined}
              title={`Артефакт ${i}: ${label}`}
              onClick={() => onArtifactClick?.(i)}
              style={
                itemW
                  ? {
                      width: itemW,
                      height: itemW,
                      flex: `0 0 ${itemW}px`,
                      fontSize: fontSizePx, // ← было 12/14px, стало адаптивно больше
                    }
                  : undefined
              }
            >
              {/* Большая цифра дня в центре */}
              <span className="leading-none">{i}</span>

              {/* Маленький бейдж-эмодзи ВНУТРИ кружка (правый нижний угол) — только если артефакт собран */}
              {collected && emoji && (
                <span
                  className="
                    absolute right-0.5 bottom-0.5
                    h-4 w-4 rounded-full bg-white
                    border border-emerald-300
                    flex items-center justify-center text-[10px] leading-none
                  "
                  aria-hidden="true"
                >
                  {emoji}
                </span>
              )}

              {/* Индикатор активности для "Сегодня" */}
              {isToday && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-400 animate-pulse" />
              )}

              {/* QUEUE-UI-A: значок "ждёт отправки" — свободный угол (левый нижний),
                  тем же приёмом absolute, что бейдж-эмодзи и индикатор "Сегодня" */}
              {isQueued && (
                <span
                  className="
                    absolute left-0.5 bottom-0.5
                    h-4 w-4 rounded-full bg-white
                    border border-sky-300
                    flex items-center justify-center text-[9px] leading-none text-sky-600
                  "
                  aria-hidden="true"
                  title="Ждёт отправки"
                >
                  ⏳
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
